/**
 * Ticket KL1: Kontaktliste – gemeinsame Typen, Beschriftungen und reine Logik.
 *
 * Bewusst ohne React und ohne Datenbankzugriff, damit Filterung und
 * Dateiaufbau testbar bleiben. Versendet wird über diese Liste nichts.
 */

import type { Database } from "@/integrations/supabase/types";
import { MANDATE_TYPE_LABELS, type MandateType } from "@/lib/client-mandate";

export type ContactOriginKind = Database["public"]["Enums"]["contact_origin_kind"];
export type ContactLegalBasis = Database["public"]["Enums"]["contact_legal_basis"];
export type ClientCategory = Database["public"]["Enums"]["client_category"];

export const CONTACT_ORIGIN_KINDS: ContactOriginKind[] = [
  "agenturkunde",
  "kurskunde",
  "interessent",
  "lead_magnet",
  "easyttpa_interessent",
  "anfrage",
  "moco_altkunde",
  "manuell",
  "event",
];

export const CONTACT_ORIGIN_LABELS: Record<ContactOriginKind, string> = {
  agenturkunde: "Agenturkunde",
  kurskunde: "Kurskunde",
  interessent: "Interessent",
  lead_magnet: "Lead-Magnet",
  easyttpa_interessent: "easyTTPA-Interessent",
  anfrage: "Anfrage",
  moco_altkunde: "Altkunde",
  manuell: "manuell erfasst",
  event: "Event",
};

export const CONTACT_LEGAL_BASES: ContactLegalBasis[] = [
  "vertrag_einwilligung",
  "ausdrueckliche_einwilligung",
  "bestandskunden_ausnahme",
];

export const CONTACT_LEGAL_BASIS_LABELS: Record<ContactLegalBasis, string> = {
  vertrag_einwilligung: "Einwilligung aus dem Vertrag",
  ausdrueckliche_einwilligung: "ausdrückliche Einwilligung",
  bestandskunden_ausnahme: "Bestandskunden-Ausnahme",
};

export const CONTACT_CLIENT_CATEGORY_LABELS: Record<ClientCategory, string> = {
  agenturkunde: "Agenturkunde",
  kurskunde: "Kurskunde",
  interessent: "Interessent",
};

export type ContactOrigin = {
  /** Kennung der Zuordnung – nötig zum Entfernen am Kontakt (Ticket KL3). */
  id: string;
  /**
   * Übergangslösung: die Art der Herkunft. Sie wird ausschließlich in der
   * Datenbank aus `sourceId` abgeleitet und nirgends von der Anwendung
   * geschrieben. Ticket KL6 entfernt sie.
   */
  kind: ContactOriginKind;
  /** Ticket KL3: Verweis auf die geführte Herkunft. */
  sourceId: string;
  sourceName: string;
  /** Deaktivierte Herkünfte bleiben an bestehenden Kontakten sichtbar. */
  sourceActive: boolean;
  /** Ticket KL3: von Hand vergeben statt automatisch entstanden. */
  addedManually: boolean;
  leadMagnet: string | null;
  occurredAt: string;
  /** Ticket: Altkunden aus Moco – Firmenname aus Moco als Herkunftsbezug. */
  mocoCompanyName?: string | null;
};


export type ContactRow = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  organization: string | null;
  clientId: string | null;
  clientName: string | null;
  clientCategory: ClientCategory | null;
  clientMandateType: MandateType | null;
  /**
   * Ticket LM4: `null` = noch keine Einwilligung. Ein solcher Kontakt existiert
   * (etwa nach dem Absenden eines Freebie-Formulars), ist aber nicht werblich
   * ansprechbar, solange der Klick auf die Einladung fehlt.
   */
  legalBasis: ContactLegalBasis | null;
  legalBasisAt: string | null;
  legalBasisWordingVersion: string | null;

  objectedAt: string | null;
  objectionNote: string | null;
  origins: ContactOrigin[];
  /** Frei vergebbare Schlagworte, alphabetisch sortiert. */
  tags: string[];
  createdAt: string;
  /**
   * Ticket VS1: letzter Pflegestand. Entscheidet bei mehreren Kontakten mit
   * derselben Adresse, welcher Datensatz den Empfänger stellt.
   */
  updatedAt?: string | null;

  /** Ticket SK11: Kontakt aus einem Kauf im Testbetrieb. */
  isTest: boolean;

  /** Ticket KL2: Sitz des Kontakts; `null` = unbekannt. */
  bundesland: Bundesland | null;
  /** Ticket KL2: bewusste Festlegung des Status; sonst wird abgeleitet. */
  statusOverride: ContactStatusOverride;
  statusOverrideAt: string | null;
  /** Anzeigename der Person, die festgelegt hat; `null` bei Läufen ohne Anmeldung. */
  statusOverrideBy: string | null;
  /** Herkunft der Festlegung, etwa "Einzelpflege" oder "Import Altbestand". */
  statusOverrideSource: string | null;
  /** Ticket KL2: bezahlter Kauf am Kontakt oder an seinem Kunden (kein Testbetrieb). */
  hasPaidPurchase: boolean;
};

/** Ticket KL2: Status der Kontaktliste – genau zwei Werte, abgeleitet. */
export type ContactStatus = "kunde" | "interessent";

export const CONTACT_STATUS_LABELS: Record<ContactStatus, string> = {
  kunde: "Kunde",
  interessent: "Interessent",
};

export type ContactStatusOverride = Database["public"]["Enums"]["contact_status_override"];

export const CONTACT_STATUS_OVERRIDES: ContactStatusOverride[] = [
  "automatisch",
  "immer_kunde",
  "immer_interessent",
];

export const CONTACT_STATUS_OVERRIDE_LABELS: Record<ContactStatusOverride, string> = {
  automatisch: "automatisch",
  immer_kunde: "immer Kunde",
  immer_interessent: "immer Interessent",
};

export type Bundesland = Database["public"]["Enums"]["de_bundesland"];

export const BUNDESLAENDER: Bundesland[] = [
  "baden_wuerttemberg",
  "bayern",
  "berlin",
  "brandenburg",
  "bremen",
  "hamburg",
  "hessen",
  "mecklenburg_vorpommern",
  "niedersachsen",
  "nordrhein_westfalen",
  "rheinland_pfalz",
  "saarland",
  "sachsen",
  "sachsen_anhalt",
  "schleswig_holstein",
  "thueringen",
];

export const BUNDESLAND_LABELS: Record<Bundesland, string> = {
  baden_wuerttemberg: "Baden-Württemberg",
  bayern: "Bayern",
  berlin: "Berlin",
  brandenburg: "Brandenburg",
  bremen: "Bremen",
  hamburg: "Hamburg",
  hessen: "Hessen",
  mecklenburg_vorpommern: "Mecklenburg-Vorpommern",
  niedersachsen: "Niedersachsen",
  nordrhein_westfalen: "Nordrhein-Westfalen",
  rheinland_pfalz: "Rheinland-Pfalz",
  saarland: "Saarland",
  sachsen: "Sachsen",
  sachsen_anhalt: "Sachsen-Anhalt",
  schleswig_holstein: "Schleswig-Holstein",
  thueringen: "Thüringen",
};

/** Filterwert und Anzeige für ein nicht gepflegtes Bundesland. */
export const BUNDESLAND_UNKNOWN = "unbekannt" as const;
export const BUNDESLAND_UNKNOWN_LABEL = "unbekannt";

export function bundeslandLabel(value: Bundesland | null): string {
  return value === null ? BUNDESLAND_UNKNOWN_LABEL : BUNDESLAND_LABELS[value];
}

/**
 * Ticket KL2: abgeleiteter Status, bewusst ohne gespeichertes Feld.
 *
 * Kunde ist, wer bezahlt hat oder als Agentur- bzw. Kurskunde geführt wird;
 * ein gesetzter Mandatstyp zählt ebenfalls. Ein Zugang zum Cockpit zählt
 * ausdrücklich NICHT: seit Ticket LM3 erhält auch ein Werkzeug-Freebie-Nutzer
 * Konto, Kunde der Kategorie "Interessent" und einen aktiven Anspruch – genau
 * diese Gruppe soll der Status ja unterscheiden. Die Kategorie "Interessent"
 * führt daher nie zu Kunde. Ein archivierter Kunde bleibt Kunde: damit ändert
 * sich die Ansprechbarkeit, nicht die Geschichte.
 */
export function deriveContactStatus(row: ContactRow): ContactStatus {
  if (row.hasPaidPurchase) return "kunde";
  if (row.clientId === null) return "interessent";
  if (row.clientCategory === "agenturkunde" || row.clientCategory === "kurskunde") return "kunde";
  if (row.clientMandateType !== null) return "kunde";
  return "interessent";
}

/** Wirksamer Status samt Angabe, ob und wovon abweichend festgelegt wurde. */
export function effectiveContactStatus(row: ContactRow): {
  status: ContactStatus;
  derived: ContactStatus;
  overridden: boolean;
} {
  const derived = deriveContactStatus(row);
  if (row.statusOverride === "immer_kunde") {
    return { status: "kunde", derived, overridden: true };
  }
  if (row.statusOverride === "immer_interessent") {
    return { status: "interessent", derived, overridden: true };
  }
  return { status: derived, derived, overridden: false };
}

export function contactStatusLabel(row: ContactRow): string {
  return CONTACT_STATUS_LABELS[effectiveContactStatus(row).status];
}



/** Vergleichsform eines Schlagworts: getrimmt, kleingeschrieben, einfache Leerzeichen. */
export function normalizeTagLabel(label: string): string {
  return label.replace(/\s+/g, " ").trim().toLowerCase();
}

/** Anzeigeform: getrimmt, einfache Leerzeichen – Groß-/Kleinschreibung bleibt. */
export function cleanTagLabel(label: string): string {
  return label.replace(/\s+/g, " ").trim();
}

/** Doppelte Schreibweisen zusammenführen und alphabetisch sortieren. */
export function dedupeTagLabels(labels: string[]): string[] {
  const map = new Map<string, string>();
  for (const raw of labels) {
    const clean = cleanTagLabel(raw);
    if (!clean) continue;
    const key = normalizeTagLabel(clean);
    if (!map.has(key)) map.set(key, clean);
  }
  return [...map.values()].sort((a, b) => a.localeCompare(b, "de"));
}


/** Ticket LM4: Filterwert für Kontakte ohne dokumentierte Einwilligung. */
export const CONTACT_LEGAL_BASIS_OPEN = "offen" as const;
export const CONTACT_LEGAL_BASIS_OPEN_LABEL = "noch offen";

export function contactLegalBasisLabel(basis: ContactLegalBasis | null): string {
  return basis === null ? CONTACT_LEGAL_BASIS_OPEN_LABEL : CONTACT_LEGAL_BASIS_LABELS[basis];
}

export type ContactFilters = {
  search?: string;
  /** Ticket KL3: Kennung der geführten Herkunft. */
  sourceId?: string | null;
  /** Konkrete, am Kontakt gespeicherte Herkunft des Lead-Magneten. */
  leadMagnet?: string | null;
  /** Ticket VS1: Herkunftsart (Ableitung an der Herkunft). */
  originKind?: ContactOriginKind | null;



  legalBasis?: ContactLegalBasis | typeof CONTACT_LEGAL_BASIS_OPEN | null;
  /** "ja" = nur widersprochene, "nein" = nur versandfähige, sonst alle. */
  objection?: "ja" | "nein" | null;
  category?: ClientCategory | null;
  mandateType?: MandateType | null;
  /** Schlagwort (Vergleich ohne Groß-/Kleinschreibung). */
  tag?: string | null;
  /** Ticket KL2: wirksamer Status (Ableitung samt Festlegung). */
  status?: ContactStatus | null;
  /** Ticket KL2: Bundesland; "unbekannt" trifft die ungepflegten Kontakte. */
  bundesland?: Bundesland | typeof BUNDESLAND_UNKNOWN | null;
};



/**
 * Ticket KL5: Sortierung der Liste. Nachname zuerst, weil das bei mehreren
 * hundert Kontakten der übliche Zugriff ist. Sortiert wird nach deutscher
 * Sortierfolge; Kontakte ohne Wert stehen unabhängig von der Richtung am Ende,
 * damit die Nachpflege nicht bei den Lücken beginnt.
 */
export type ContactSortKey = "lastName" | "firstName" | "email";
export type ContactSortDirection = "asc" | "desc";

export const CONTACT_SORT_LABELS: Record<ContactSortKey, string> = {
  lastName: "Nachname",
  firstName: "Vorname",
  email: "E-Mail-Adresse",
};

export function sortContacts(
  rows: ContactRow[],
  key: ContactSortKey,
  direction: ContactSortDirection,
): ContactRow[] {
  const factor = direction === "asc" ? 1 : -1;
  const value = (row: ContactRow): string =>
    (key === "lastName" ? row.lastName : key === "firstName" ? row.firstName : row.email).trim();

  return [...rows].sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    if (va === "" && vb === "") return a.email.localeCompare(b.email, "de-DE");
    if (va === "") return 1;
    if (vb === "") return -1;
    const primary = va.localeCompare(vb, "de-DE", { sensitivity: "base" });
    if (primary !== 0) return primary * factor;
    // Gleichstand: die Adresse entscheidet, damit die Reihenfolge stabil ist.
    return a.email.localeCompare(b.email, "de-DE") * factor;
  });
}

/**
 * Ticket KL5, Punkt 6: der verwendete Filter im Klartext. Er wandert
 * unverändert ins Änderungsprotokoll, damit später nachlesbar ist, auf welche
 * Auswahl eine Sammelaktion gewirkt hat.
 */
export function describeContactFilters(
  filters: ContactFilters,
  options: { sourceNames?: Record<string, string>; includeTest?: boolean } = {},
): string {
  const parts: string[] = [];
  const search = (filters.search ?? "").trim();
  if (search) parts.push(`Suche: „${search}"`);
  if (filters.status) parts.push(`Status: ${CONTACT_STATUS_LABELS[filters.status]}`);
  if (filters.sourceId) {
    parts.push(`Herkunft: ${options.sourceNames?.[filters.sourceId] ?? filters.sourceId}`);
  }
  if (filters.leadMagnet) parts.push(`Herkunftseintrag: ${filters.leadMagnet}`);
  if (filters.originKind) parts.push(`Herkunftsart: ${CONTACT_ORIGIN_LABELS[filters.originKind]}`);

  if (filters.bundesland) {
    parts.push(
      `Bundesland: ${
        filters.bundesland === BUNDESLAND_UNKNOWN
          ? BUNDESLAND_UNKNOWN_LABEL
          : BUNDESLAND_LABELS[filters.bundesland]
      }`,
    );
  }
  if (filters.legalBasis) {
    parts.push(
      `Rechtsgrundlage: ${
        filters.legalBasis === CONTACT_LEGAL_BASIS_OPEN
          ? CONTACT_LEGAL_BASIS_OPEN_LABEL
          : CONTACT_LEGAL_BASIS_LABELS[filters.legalBasis]
      }`,
    );
  }
  if (filters.objection === "ja") parts.push("nur mit Widerspruch");
  if (filters.objection === "nein") parts.push("nur ohne Widerspruch");
  if (filters.category) parts.push(`Kategorie: ${CONTACT_CLIENT_CATEGORY_LABELS[filters.category]}`);
  if (filters.mandateType) parts.push(`Mandatstyp: ${MANDATE_TYPE_LABELS[filters.mandateType]}`);
  if (filters.tag) parts.push(`Schlagwort: ${filters.tag}`);
  if (options.includeTest) parts.push("Testdaten eingeblendet");
  return parts.length === 0 ? "kein Filter (gesamter Bestand)" : parts.join(" · ");
}

export function contactDisplayName(row: ContactRow): string {
  const name = `${row.firstName} ${row.lastName}`.trim();
  return name || row.email;
}

/**
 * Werblich ansprechbar ist nur, wer eine dokumentierte Rechtsgrundlage hat und
 * nicht widersprochen hat. Ein Kontakt ohne Rechtsgrundlage (Ticket LM4) wird
 * von keiner Segment-Auswahl erfasst.
 */
export function isContactMailable(row: ContactRow): boolean {
  return row.objectedAt === null && row.legalBasis !== null;
}

export function filterContacts(rows: ContactRow[], filters: ContactFilters): ContactRow[] {
  const needle = (filters.search ?? "").trim().toLowerCase();
  const tagNeedle = filters.tag ? normalizeTagLabel(filters.tag) : "";
  return rows.filter((row) => {
    if (needle) {
      const haystack = [
        row.email,
        row.firstName,
        row.lastName,
        row.organization ?? "",
        row.clientName ?? "",
        ...(row.tags ?? []),
      ]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    if (tagNeedle && !(row.tags ?? []).some((t) => normalizeTagLabel(t) === tagNeedle)) return false;
    if (filters.sourceId && !row.origins.some((o) => o.sourceId === filters.sourceId)) return false;
    if (filters.leadMagnet && !row.origins.some((o) => o.leadMagnet === filters.leadMagnet && (!filters.sourceId || o.sourceId === filters.sourceId))) return false;
    if (filters.originKind && !row.origins.some((o) => o.kind === filters.originKind)) return false;

    if (filters.legalBasis === CONTACT_LEGAL_BASIS_OPEN) {
      if (row.legalBasis !== null) return false;
    } else if (filters.legalBasis && row.legalBasis !== filters.legalBasis) return false;
    if (filters.objection === "ja" && row.objectedAt === null) return false;
    if (filters.objection === "nein" && (row.objectedAt !== null || row.legalBasis === null)) return false;

    // Kategorie: „Interessent“ trifft jeden Kontakt, der nach der Ableitung
    // Interessent ist – auch ohne hinterlegten Kunden-Datensatz. Vorher las der
    // Filter allein die Kategorie des Kunden-Datensatzes; Kontakte ohne Zugang
    // (die große Mehrheit) fielen damit aus der Segmentierung heraus.
    if (filters.category === "interessent") {
      if (effectiveContactStatus(row).status !== "interessent") return false;
    } else if (filters.category && row.clientCategory !== filters.category) return false;
    if (filters.mandateType && row.clientMandateType !== filters.mandateType) return false;
    if (filters.status && effectiveContactStatus(row).status !== filters.status) return false;
    if (filters.bundesland === BUNDESLAND_UNKNOWN) {
      if (row.bundesland !== null) return false;
    } else if (filters.bundesland && row.bundesland !== filters.bundesland) return false;
    return true;

  });
}


function csvCell(value: string): string {
  const needsQuotes = /[";\n\r]/.test(value);
  const escaped = value.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
}

export function formatOrigin(
  origin: Pick<ContactOrigin, "kind" | "leadMagnet"> &
    Partial<Pick<ContactOrigin, "sourceName" | "mocoCompanyName">>,
): string {
  // Ticket KL3: die gepflegte Bezeichnung gewinnt; die Art dient nur als Netz.
  const label = origin.sourceName || CONTACT_ORIGIN_LABELS[origin.kind];
  const detail = origin.leadMagnet ?? origin.mocoCompanyName ?? null;
  return detail ? `${label} (${detail})` : label;
}


export const CONTACT_CSV_HEADER = [
  "E-Mail",
  "Vorname",
  "Nachname",
  "Organisation",
  "Status",
  "Statusfestlegung",
  "Festgelegt am",
  "Festgelegt durch",
  "Bundesland",
  "Kunde",
  "Kundenkategorie",
  "Mandatstyp",
  "Rechtsgrundlage",
  "Rechtsgrundlage am",
  "Wortlaut-Version",
  "Widerspruch am",
  "Widerspruchsvermerk",
  "Herkuenfte",
  "Schlagworte",
  "Angelegt am",

];


/** Ausleiten als Datei – nur für Administratoren (Prüfung in der Serverfunktion). */
export function contactsToCsv(rows: ContactRow[]): string {
  const lines = [CONTACT_CSV_HEADER.join(";")];
  for (const row of rows) {
    lines.push(
      [
        row.email,
        row.firstName,
        row.lastName,
        row.organization ?? "",
        contactStatusLabel(row),
        CONTACT_STATUS_OVERRIDE_LABELS[row.statusOverride],
        row.statusOverrideAt ?? "",
        row.statusOverrideBy ?? row.statusOverrideSource ?? "",
        bundeslandLabel(row.bundesland),
        row.clientName ?? "",

        row.clientCategory ? CONTACT_CLIENT_CATEGORY_LABELS[row.clientCategory] : "",
        row.clientMandateType ? MANDATE_TYPE_LABELS[row.clientMandateType] : "",
        contactLegalBasisLabel(row.legalBasis),
        row.legalBasisAt ?? "",

        row.legalBasisWordingVersion ?? "",
        row.objectedAt ?? "",
        row.objectionNote ?? "",
        row.origins.map(formatOrigin).join(" | "),
        (row.tags ?? []).join(" | "),
        row.createdAt,

      ]
        .map((v) => csvCell(String(v)))
        .join(";"),
    );
  }
  return lines.join("\r\n");
}

export function normalizeContactEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Ticket VS5: Empfängergruppen. Innerhalb einer Gruppe gilt „und“ (wie
 * `filterContacts`), zwischen den Gruppen „oder“. Doppelte Adressen werden
 * nicht hier, sondern ausschließlich in `buildRecipientSelection` aufgelöst.
 */
export type RecipientFilterSet = { groups: ContactFilters[] };
export type StoredRecipientFilters = ContactFilters | RecipientFilterSet;
export const RECIPIENT_MAX_GROUPS = 10;
/** Höchstlänge eines Schlagworts im Empfängerfilter (Freebie-Titel werden zu Schlagwörtern). */
export const RECIPIENT_TAG_MAX = 200;

function isGroupEmpty(group: ContactFilters): boolean {
  return !Object.values(group).some((v) => v !== null && v !== undefined && v !== "");
}

export function isRecipientFilterSet(raw: unknown): raw is RecipientFilterSet {
  return (
    typeof raw === "object" && raw !== null && Array.isArray((raw as { groups?: unknown }).groups)
  );
}

/** Alte flache Filter werden eine Gruppe; leere Gruppen fallen weg (mind. eine bleibt). */
export function normalizeRecipientFilters(raw: unknown): RecipientFilterSet {
  const groups = isRecipientFilterSet(raw)
    ? raw.groups
    : [((raw ?? {}) as ContactFilters)];
  const kept = groups.filter((g) => g && !isGroupEmpty(g)).slice(0, RECIPIENT_MAX_GROUPS);
  return { groups: kept.length > 0 ? kept : [{}] };
}

/** Vereinigung je Kontakt: jede Zeile höchstens einmal; `overlap` = in mehreren Gruppen. */
export function filterContactsAnyOf(
  rows: ContactRow[],
  groups: ContactFilters[],
): { rows: ContactRow[]; overlap: number } {
  const hits = new Map<string, number>();
  for (const group of groups) {
    for (const row of filterContacts(rows, group)) hits.set(row.id, (hits.get(row.id) ?? 0) + 1);
  }
  let overlap = 0;
  for (const n of hits.values()) if (n > 1) overlap += 1;
  return { rows: rows.filter((row) => hits.has(row.id)), overlap };
}

export function describeRecipientFilters(
  raw: unknown,
  options: { sourceNames?: Record<string, string>; includeTest?: boolean } = {},
): string {
  const { groups } = normalizeRecipientFilters(raw);
  if (groups.length === 1) return describeContactFilters(groups[0], options);
  return groups
    .map((g, i) => `Gruppe ${i + 1}: ${describeContactFilters(g, options)}`)
    .join(" ODER ");
}
